interface ServiceLike {
  ser_name: string;
  ser_description?: string;
  ser_pubsub?: string;
}

interface ServiceResultLike {
  result?: string;
  /**
   * Nome do serviço, como o cliente o conhece — já vem pronto do api-balcao.
   *
   * Antes isto era um código interno de 3 letras ("bri", "bac"), derivado do
   * `ser_pubsub`, e este arquivo fazia o de-para para o nome. O código não
   * aparecia na tela, mas viajava na resposta HTTP: bastava abrir o
   * inspecionar do navegador para ler a nomenclatura interna da integração.
   *
   * O de-para foi para o servidor. Aqui o valor já é o nome, e a comparação
   * entre resultado e serviço contratado passou a ser por nome.
   *
   * Vem nulo quando a flag "mostrar qual serviço apontou" está desligada.
   */
  service?: string;
  additionalData?: unknown[];
}

/**
 * Nome comparável de um serviço. Normaliza para tolerar diferença de caixa e
 * de espaços entre o que o backend devolve e o que está no cadastro.
 */
const normalize = (value?: string): string =>
  (value ?? '').trim().toLowerCase();

/** Como o serviço contratado se chama para o cliente. */
export const serviceLabelOf = (service: ServiceLike): string =>
  service.ser_description || service.ser_name;

/** Um resultado pertence a este serviço? */
const isFromService = (
  result: ServiceResultLike,
  service: ServiceLike,
): boolean => {
  const alvo = normalize(result.service);
  if (!alvo) return false;

  return (
    alvo === normalize(service.ser_description) ||
    alvo === normalize(service.ser_name)
  );
};

/**
 * Quantos apontamentos um serviço trouxe. Conta só resultados confirmados
 * ('error' — Reprovado). Um resultado 'warn' (falha, incluindo o caso de
 * timeout parcial que capturou apontamentos antes de falhar) não entra aqui
 * de propósito: a empresa não cobra consultas com falha, e contar/mostrar
 * apontamentos que vieram de uma falha desestimularia o cliente a pedir a
 * consulta de novo.
 */
export const findingsCountOf = (result: ServiceResultLike): number => {
  if (result.result !== 'error') return 0;

  return result.additionalData?.length || 1;
};

/**
 * Situação de um serviço nesta consulta.
 *
 * - `pending`  — ainda não há resultado para ele. Acontece antes da consulta
 *                rodar E também quando a flag "mostrar qual serviço apontou"
 *                está desligada: nesse caso o backend devolve `service: null`
 *                em todos os resultados e não há como saber de quem é o quê.
 * - `findings` — voltou Reprovado, com apontamento.
 * - `failed`   — a consulta desse serviço falhou. NÃO é o mesmo que limpo: não
 *                sabemos o que teria voltado, então pintar de verde seria
 *                afirmar algo que não foi apurado.
 * - `clean`    — rodou e não achou nada.
 */
export type ServiceOutcome = 'pending' | 'findings' | 'failed' | 'clean';

export const serviceOutcomeOf = (
  results: ServiceResultLike[],
  service: ServiceLike,
): ServiceOutcome => {
  const own = results.filter(item => isFromService(item, service));

  if (own.length === 0) return 'pending';
  if (own.some(item => item.result === 'error')) return 'findings';
  if (own.some(item => item.result === 'warn')) return 'failed';

  return 'clean';
};

/** Soma os apontamentos de todos os resultados de um mesmo serviço. */
export const findingsCountForService = (
  results: ServiceResultLike[],
  service: ServiceLike,
): number =>
  results
    .filter(item => isFromService(item, service))
    .reduce((total, item) => total + findingsCountOf(item), 0);

/**
 * Faixa de severidade pela quantidade de apontamentos. Três níveis, com a
 * mesma rampa de intensidade das classes do Score (ScoreBand).
 */
export type FindingsSeverity = 'none' | 'low' | 'medium' | 'high';

export const severityForCount = (count: number): FindingsSeverity => {
  if (count <= 0) return 'none';
  if (count <= 2) return 'low';
  if (count <= 5) return 'medium';
  return 'high';
};
